import { useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpenIcon, ClockIcon } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  cn,
} from '@sandtech-hub-monorepo/ui-kit';
import type { GetViewerAvailableProgramsQuery } from '@sandtech-hub-monorepo/ehub-client';
import { useGetMaybeViewer } from '@sandtech-hub-monorepo/ui-auth-kit';
import programBanner from '../../assets/banners/program-banner.webp';
import { ReactComponent as RewardsIcon } from '../../assets/Icons/reward.svg';
import { ProgramEnrollmentModal } from './ProgramEnrollmentModal';
import { enrollButtonState } from '../enrollButtonState';

type Program = NonNullable<
  GetViewerAvailableProgramsQuery['viewer']['availablePrograms']
>[number];

interface Props {
  program: Program;
  programPageLink: string;
  learningPageLink: string;
  className?: string;
}

export const RelatedProgramCard = ({
  program,
  programPageLink,
  learningPageLink,
  className,
}: Props) => {
  const [modalOpen, setModalOpen] = useState(false);
  const { viewer } = useGetMaybeViewer();

  const coursesCount =
    program.potentialProgramJourney?.potentialCourseEnrollments?.length ?? 0;

  const detailsHref = programPageLink.replace(
    ':programId',
    program.abbreviation
  );

  const buttonState = enrollButtonState(
    program.isEnrolled,
    program.isGraduated,
    program.canEnroll,
    program.isUniversityProgram
  );

  return (
    <>
      <Card
        className={cn(
          'group flex flex-col overflow-hidden py-0 gap-0 transition-shadow hover:shadow-lg',
          className
        )}
      >
        <div className="relative h-44 overflow-hidden">
          <img
            src={program.marketing.thumbnail || programBanner}
            alt={program.name}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-secondary/60 via-secondary/20 to-transparent" />
          {program.isUniversityProgram && (
            <div className="absolute bottom-3 left-3 flex items-center gap-1.5">
              <Badge className="bg-card/90 text-foreground backdrop-blur-sm">
                University Program
              </Badge>
            </div>
          )}
        </div>

        <CardHeader className="pt-4 pb-1">
          <CardTitle className="text-base leading-snug">
            {program.name}
          </CardTitle>
          <CardDescription className="line-clamp-2 text-xs">
            {program.description}
          </CardDescription>
        </CardHeader>

        <CardContent className="mt-auto pt-4 pb-2">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs font-medium text-foreground/70">
            {program.marketing.programDuration && (
              <span className="inline-flex items-center gap-1">
                <ClockIcon className="w-3 h-3" />
                {program.marketing.programDuration}
              </span>
            )}
            {coursesCount > 0 && (
              <span className="inline-flex items-center gap-1">
                <BookOpenIcon className="w-3 h-3" />
                {coursesCount} {coursesCount === 1 ? 'course' : 'courses'}
              </span>
            )}
            {program.marketing.legacyPoints && (
              <span className="inline-flex items-center gap-1">
                <RewardsIcon className="w-3 h-3" />
                {program.marketing.legacyPoints}
              </span>
            )}
          </div>
        </CardContent>

        <CardFooter className="flex gap-4 pt-0 pb-6">
          <Link to={detailsHref} className="flex-1">
            <Button variant="outline" className="w-full">
              View Details
            </Button>
          </Link>
          <Button
            variant="enhanced"
            className="flex-1"
            disabled={buttonState.disabled}
            onClick={() => setModalOpen(true)}
          >
            {buttonState.ctaText}
          </Button>
        </CardFooter>
      </Card>
      <ProgramEnrollmentModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        program={program}
        learningPageLink={learningPageLink}
        viewer={viewer}
      />
    </>
  );
};
